import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ActionSheetController,
  IonContent,
  ModalController,
  ViewDidEnter,
  ViewDidLeave,
  ViewWillEnter,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '@notifications/services/notifications.service';
import {
  PanAppState,
  PlatformEnum,
  PlatformService,
  SelectedCustomerState,
} from '@panamax/app-state';
import { AppService } from '@shared/services/app.service';
import {
  DownloadListOptions,
  ListConstants,
  initialListOfListsSort,
} from '@usf/ngrx-list';
import {
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  of,
  Subscription,
  take,
} from 'rxjs';
import { HelpModalDesktopComponent } from '../shared/components/help-modal/help-modal-desktop/help-modal-desktop.component';
import { HelpModalTouchComponent } from '../shared/components/help-modal/help-modal-touch/help-modal-touch.component';
import { ModalWithNavComponent } from '../shared/components/modal-with-nav/modal-with-nav.component';
import { SortDirections } from '../shared/components/sortable-section/models/sortable-section.model';
import { ListTypes } from '../shared/constants/lists-constants';
import { PageTitle } from '../shared/constants/page-titles.enum';
import { PATHS } from '../shared/constants/paths';
import { FEATURES } from '../shared/constants/splitio-features';
import { ModalService } from '../shared/services/modal/modal.service';
import { ListsPageData } from './model/lists-page.model';
import { MasterListService } from './pages/master-list/services/master-list.service';
import { ListsOverlayService } from './services/lists-overlay.service';
import { ListsService } from './services/lists.service';
import { SearchProductNumbersService } from './services/search-product-numbers.service';
import { MassDeleteDualPaneContainerComponent } from './shared/delete-list/mass-delete-dual-pane-container/mass-delete-dual-pane-container.component';
import { ListDetailManagementService } from './shared/list-detail-management/services/list-detail-management.service';
import { ImportListModalContainerComponent } from './components/import-list-modal-container/import-list-modal-container.component';
import { UserKinds } from '@usf/user-types/user';
import { ListUpdatesService } from './services/list-updates.service';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { ListDownloadContainerComponent } from './shared/list-download-container/list-download-container.component';
import { ListPrintContainerComponent } from './shared/list-print-container/list-print-container.component';
import {
  AiListBuilderModalOptions,
  AiListMetaDataService,
  AIListService,
} from '@usf/ngrx-list-ai';
import { SimpleSearchModel } from '@shared/components/new-search-simple-modal/new-search-simple-modal.component';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';
import { NonUsfProductViewModelSelectors } from '@inventory/pages/manage-non-usf-products/selectors/non-usf-selectors';
import { manageNonUsfProductsViewModel } from '@inventory/pages/manage-non-usf-products/models/non-usf-products-view-model';
import { Button } from '@shared/models/ellipsis-option.model';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { AddToListModalService } from '@shared/services/dual-pane/add-to-list-modal.service';
import {
  formatSectionTitle,
  SectionTitleKey,
} from './helpers/section-title-format.helper';

@Component({
  selector: 'app-lists',
  templateUrl: './lists.page.html',
  styleUrls: ['./lists.page.scss'],
})
export class ListsPage
  implements ViewWillEnter, ViewDidEnter, ViewDidLeave, OnInit
{
  // child components
  @ViewChild(IonContent) content: IonContent;
  @ViewChild(MassDeleteDualPaneContainerComponent)
  massDeleteModal: MassDeleteDualPaneContainerComponent;
  @ViewChild(ImportListModalContainerComponent)
  importListModal: ImportListModalContainerComponent;
  @ViewChild(ListDownloadContainerComponent)
  listDownloadModal: ListDownloadContainerComponent;
  @ViewChild(ListPrintContainerComponent)
  listPrintModal: ListPrintContainerComponent;

  dismissPromise: Promise<string>;
  private resolveDismissPromise!: Function; // To hold the resolve function

  // static UI
  readonly userKinds = UserKinds;
  pageTitle: string;
  backgroundImageStyle = "url('/assets/images/list-hero.webp')";
  overlayGradient =
    'linear-gradient(90deg, rgba(47,47,47,0.8) 0%, rgba(47,47,47,0.77) 8%, rgba(47,47,47,0.5) 40%, rgba(47,47,47,0) 53%, rgba(47,47,47,0) 77%, rgba(47,47,47,0) 89%, rgba(47,47,47,0) 100%)';

  // observables
  listsPageData$: Observable<ListsPageData>;
  addProductsFeatureFlag$: Observable<boolean>;
  selectedCustomer$: Observable<SelectedCustomerState>;
  listNameBlackList$: Observable<Set<string>>;
  totalNonUsfProducts$: Observable<number>;
  addNonUsfListFlag$: Observable<boolean>;
  isNewSearchEnabledSubscription$: Subscription;

  // search properties
  searchKey = '';
  totalLists = 0;

  // other properties
  platform: PlatformEnum;
  addProductsActionSheetIsOpen = false;
  addProductsButtons$: Observable<Button[]> = of([]);
  listUpdatesExist$: Observable<boolean>;
  isSearchModalOpen = false;
  isNewSearchEnabled = false;
  managedByUsfSectionTitle: string;

  constructor(
    public actionSheetController: ActionSheetController,
    private modalController: ModalController,
    private panAppState: PanAppState,
    private appService: AppService,
    readonly listsOverlayService: ListsOverlayService,
    readonly listsService: ListsService,
    private modalService: ModalService,
    readonly platformService: PlatformService,
    readonly translateService: TranslateService,
    private notificationsService: NotificationsService,
    private masterListService: MasterListService,
    public listDetailManagementService: ListDetailManagementService,
    private router: Router,
    private listUpdateService: ListUpdatesService,
    private navigationHelperService: NavigationHelperService,
    readonly aiListMetaDataService: AiListMetaDataService,
    private readonly aiListService: AIListService,
    private nonUsfProductViewModelSelectors: NonUsfProductViewModelSelectors,
    private readonly toggleNewSearchService: ToggleNewSearchService,
    readonly addToListModalService: AddToListModalService,
  ) {
    this.listNameBlackList$ = this.listsService.listNameBlackList$;
    this.resetDismissPromise();
    this.totalNonUsfProducts$ =
      this.nonUsfProductViewModelSelectors.totalNonUsfProducts$;
  }

  ngOnInit() {
    this.setFormattedSectionTitles();
  }

  ionViewWillEnter() {
    this.isNewSearchEnabledSubscription$ = this.toggleNewSearchService
      .isNewSearchEnabled()
      .subscribe(newSearchEnabled => {
        this.isNewSearchEnabled = newSearchEnabled;
      });
    this.platform = this.platformService.platformType;
    this.listUpdatesExist$ = this.listUpdateService.listUpdatesExist$();
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.lists),
    );
    this.pageTitle = this.translateService.instant(PageTitle.lists);
    this.searchKey = '';
    this.listsPageData$ = this.listsService.listsView$(this.platform);
    this.notificationsService.checkMissedNotification();
    this.addProductsFeatureFlag$ = this.panAppState.feature$([
      FEATURES.split_global_add_multiple_products.name,
    ]);
    this.selectedCustomer$ = this.panAppState.customer$;
    this.addNonUsfListFlag$ = this.panAppState.feature$([
      FEATURES.split_dynamic_add_non_usf_list.name,
    ]);
    this.getAddProductsButtons$();
  }

  ionViewDidEnter() {
    this.appService.getCustomFeedback('no-meta-reducer');
    this.listsService.trackListsPageLoad();
    this.content?.scrollToTop();
    this.listsService.resetRecentPurchaseSelections();
  }

  private setFormattedSectionTitles() {
    const managedByUsf = this.translateService.instant(
      'i18n.lists.managedByUsf',
    );
    this.managedByUsfSectionTitle = formatSectionTitle(
      'i18n.lists.managedByUsf',
      managedByUsf,
    );
  }

  goToListUpdates() {
    this.navigationHelperService.routeTo(PATHS.LIST_UPDATES);
  }

  search() {
    // if user manually clears search field
    if (this.searchKey === '') {
      this.resetSearch();
      return;
    }
    this.listsPageData$ = this.listsService.listsView$(
      this.platform,
      this.searchKey,
    );
  }

  resetSearch() {
    this.searchKey = '';
    this.listsPageData$ = this.listsService.listsView$(
      this.platform,
      this.searchKey,
    );
  }

  getAddProductsButtons$() {
    this.addProductsButtons$ = combineLatest([
      this.totalNonUsfProducts$,
      this.addNonUsfListFlag$,
    ]).pipe(
      map(([productCount, flag]) => {
        const addProductsButtons: Button[] = [
          {
            text: this.translateService.instant('i18n.lists.addToListSearch'),
            handler: () => {
              if (this.isNewSearchEnabled) {
                this.isSearchModalOpen = true;
              } else {
                this.listsOverlayService.executeSearch();
              }
            },
          },
          {
            text: this.translateService.instant('i18n.lists.addToListMultiple'),
            handler: () => {
              this.router.navigate([PATHS.LISTS_SEARCH_PRODUCT_NUMBERS]);
            },
          },
        ];
        if (productCount > 0 && flag) {
          addProductsButtons.push({
            text: this.translateService.instant(
              'i18n.inventory.inventoryWorksheet.addNonUSFoodsProducts',
            ),
            handler: () => {
              this.openAddToListModal();
            },
          });
        }
        if (this.platform !== PlatformEnum.desktop) {
          addProductsButtons.push({
            text: this.translateService.instant('i18n.common.cancel'),
            role: 'cancel',
            cssClass: 'cancel-button',
          });
        }
        return addProductsButtons;
      }),
    );
  }

  handleSimpleSearchModalEvent(event: SimpleSearchModel) {
    this.isSearchModalOpen = false;
    if (event?.isExecuteSearch) {
      this.listsOverlayService.executeSearch(
        SEARCH_PAGES_ENUM.list,
        event?.searchText,
      );
    }
  }

  async openMobileSort(headers: any) {
    let sortDirection: string;
    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('i18n.lists.listNameDefault'),
          handler: () => {
            sortDirection = headers.find(
              (header: { headerName: string }) =>
                header.headerName === ListConstants.listName,
            ).sortDirection;
            this.updateListOfListsSortMobile(
              ListConstants.listName,
              sortDirection,
            );
          },
        },
        {
          text: this.translateService.instant('i18n.common.lastUpdatedBy'),
          handler: () => {
            sortDirection = headers.find(
              (header: { headerName: string }) =>
                header.headerName === ListConstants.lastUpdatedBy,
            ).sortDirection;
            this.updateListOfListsSortMobile(
              ListConstants.lastUpdatedBy,
              sortDirection,
            );
          },
        },
        {
          text: this.translateService.instant('i18n.lists.products'),
          handler: () => {
            sortDirection = headers.find(
              (header: { headerName: string }) =>
                header.headerName === ListConstants.numProducts,
            ).sortDirection;
            this.updateListOfListsSortMobile(
              ListConstants.numProducts,
              sortDirection,
            );
          },
        },
        {
          text: this.translateService.instant(
            'i18n.lists.discontinuedProducts',
          ),
          handler: () => {
            sortDirection = headers.find(
              (header: { headerName: string }) =>
                header.headerName === ListConstants.numDiscontinued,
            ).sortDirection;
            this.updateListOfListsSortMobile(
              ListConstants.numDiscontinued,
              sortDirection,
            );
          },
        },
        {
          text: this.translateService.instant('i18n.common.cancel'),
          role: 'cancel',
          cssClass: 'cancel-button',
        },
      ],
    });
    await actionSheet.present();
  }

  updateListOfListsSortMobile(headerName: string, sortDirection: string) {
    let updatedSortDirection =
      sortDirection === SortDirections.asc
        ? SortDirections.desc
        : SortDirections.asc;

    Object.keys(initialListOfListsSort).forEach(key => {
      this.listsService.updateListOfListsSort(
        key,
        headerName,
        updatedSortDirection,
      );
    });
  }

  async openCreateListModal(listData: ListsPageData) {
    this.listsOverlayService.openCreateListOptions(
      listData,
      this.importListModal,
    );
  }

  routeToManageVendorsPage(path: string[]) {
    this.listsService.goToShoppingList(path);
  }

  resetDismissPromise() {
    this.dismissPromise = new Promise<string>((resolve, reject) => {
      this.resolveDismissPromise = resolve; // Save the resolve function
    });
  }

  clearAiListPhotosAndErrors() {
    if (this.resolveDismissPromise) {
      this.resolveDismissPromise();
    }
  }

  async openHelpModal() {
    if (this.platformService.isTouch.value) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: HelpModalTouchComponent,
      });
    } else {
      this.modalService.setModalOptions(
        false,
        ModalWithNavComponent,
        {
          rootPage: HelpModalDesktopComponent,
        },
        'modal-desktop-size-xl',
      );
    }
    this.resetDismissPromise();
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  ionViewDidLeave(): void {
    this.listsService.resetListOfListsSort();
    this.isNewSearchEnabledSubscription$?.unsubscribe();
  }

  closeDownloadListModal() {
    this.listsOverlayService.closeDownloadListModal();
  }

  downloadList(options: DownloadListOptions) {
    this.listsOverlayService.closeDownloadListModal();
    if (options.listTypeId === ListTypes.masterList) {
      this.masterListService.downloadMasterList(options);
    } else {
      this.listDetailManagementService.downloadList(options);
    }
  }

  openAddProducts() {
    if (this.platform !== PlatformEnum.desktop) {
      this.addProductsActionSheetIsOpen = true;
    }
  }

  createAiList(options: AiListBuilderModalOptions) {
    this.listsOverlayService.closeAiListBuilderModal();
    this.aiListService.create(options);
  }

  openAddToListModal() {
    this.addToListModalService.openModalForNonUsf();
  }
}
